/* ═══════════════════════════════════════════════════════════
   URGO THEME — Main JavaScript
   Theme toggle · Sticky nav · Mobile menu · Scroll reveal
   Product gallery · Cart drawer · Newsletter · Animations
═══════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── Theme Toggle ──────────────────────────────────────── */
  const html = document.documentElement;
  const themeKey = 'urgo-theme';
  let currentTheme = localStorage.getItem(themeKey)
    || (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

  applyTheme(currentTheme);

  function applyTheme(theme) {
    html.setAttribute('data-theme', theme);
    currentTheme = theme;
    localStorage.setItem(themeKey, theme);
    document.querySelectorAll('[data-theme-toggle]').forEach(updateToggleBtn);
  }

  function updateToggleBtn(btn) {
    const isDark = currentTheme === 'dark';
    btn.setAttribute('aria-label', 'Switch to ' + (isDark ? 'light' : 'dark') + ' mode');
    btn.innerHTML = isDark
      ? `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
           <circle cx="12" cy="12" r="5"/>
           <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
         </svg>`
      : `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
           <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
         </svg>`;
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-theme-toggle]')) {
      applyTheme(currentTheme === 'dark' ? 'light' : 'dark');
    }
  });

  /* ── Sticky Nav ────────────────────────────────────────── */
  const nav = document.querySelector('.urgo-nav');
  if (nav) {
    const onScroll = () => nav.classList.toggle('scrolled', window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ── Mobile Menu ───────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    const hamburger = e.target.closest('[data-hamburger]');
    const mobileNav = document.getElementById('urgo-mobile-nav');
    if (!hamburger || !mobileNav) return;

    const isOpen = mobileNav.classList.toggle('open');
    hamburger.setAttribute('aria-expanded', String(isOpen));

    const spans = hamburger.querySelectorAll('span');
    spans.forEach((s, i) => {
      s.style.transform = isOpen
        ? (i === 0 ? 'rotate(45deg) translate(4px, 4px)' : i === 2 ? 'rotate(-45deg) translate(4px, -4px)' : 'scale(0)')
        : '';
      if (i === 1) s.style.opacity = isOpen ? '0' : '1';
    });
  });

  // Close mobile nav on link click
  document.addEventListener('click', function (e) {
    if (e.target.closest('#urgo-mobile-nav a')) {
      const mobileNav = document.getElementById('urgo-mobile-nav');
      const hamburger = document.querySelector('[data-hamburger]');
      if (mobileNav) mobileNav.classList.remove('open');
      if (hamburger) hamburger.setAttribute('aria-expanded', 'false');
    }
  });

  /* ── Scroll Reveal ─────────────────────────────────────── */
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
  } else {
    document.querySelectorAll('.reveal').forEach(el => el.classList.add('in-view'));
  }

  /* ── Product Gallery ───────────────────────────────────── */
  const gallery = document.querySelector('.urgo-product-gallery');
  if (gallery) {
    const mainImg = gallery.querySelector('.urgo-product-gallery__main img');
    const thumbs = gallery.querySelectorAll('.urgo-product-gallery__thumb');

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const src = thumb.querySelector('img')?.src;
        if (src && mainImg) mainImg.src = src;
        thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
      });
    });
  }

  /* ── Add to Cart feedback ──────────────────────────────── */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('.urgo-product-card__add');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const orig = btn.innerHTML;
    btn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <path d="M22 4L12 14.01l-3-3"/>
    </svg>`;
    btn.style.background = '#009E85';

    // Update cart count
    const counter = document.querySelector('.urgo-nav__cart-count');
    if (counter) {
      const n = (parseInt(counter.textContent, 10) || 0) + 1;
      counter.textContent = n;
      counter.hidden = false;
      counter.style.animation = 'none';
      counter.offsetHeight; // reflow
      counter.style.animation = '';
    }

    setTimeout(() => {
      btn.innerHTML = orig;
      btn.style.background = '';
    }, 1600);
  });

  /* ── Newsletter form ───────────────────────────────────── */
  document.addEventListener('submit', function (e) {
    const form = e.target.closest('[data-newsletter-form]');
    if (!form) return;
    e.preventDefault();

    const email = form.querySelector('input[type="email"]');
    const btn = form.querySelector('button[type="submit"]');
    if (!email || !btn) return;

    btn.textContent = '✓ You\'re in!';
    btn.style.background = '#0A0A0A';
    btn.style.color = 'white';
    email.value = '';
    email.placeholder = 'Thanks! Check your inbox.';
    email.disabled = true;
    btn.disabled = true;
  });

  /* ── Smooth scroll for anchors ─────────────────────────── */
  document.addEventListener('click', function (e) {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;
    const id = link.getAttribute('href');
    const target = id === '#' ? null : document.querySelector(id);
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  /* ── Quantity selectors (product page) ─────────────────── */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    const input = btn.closest('.urgo-qty')?.querySelector('input');
    if (!input) return;
    const delta = btn.dataset.qtyChange === '+' ? 1 : -1;
    const newVal = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
    input.value = newVal;
  });

  /* ── Announcement bar dismiss ──────────────────────────── */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-dismiss-announcement]');
    if (btn) {
      const bar = document.querySelector('.urgo-announcement');
      if (bar) bar.style.display = 'none';
    }
  });

})();
